import React, { useState, useEffect } from 'react'

interface Organization {
  id: string
  name: string
}

interface ExternalSystem {
  id?: number
  organization: string
  organization_name?: string
  name: string
  system_type: string
  vendor: string
  version: string
  endpoint_url: string
  connection_config: Record<string, any>
  auth_type: string
  auth_config: Record<string, any>
  status: string
  health_status?: string
  timeout_seconds: number
  retry_attempts: number
  rate_limit_per_minute: number | null
  is_healthy?: boolean
  created_at?: string
  last_health_check?: string
}

interface IntegrationLog {
  id: number
  log_id: string
  external_system_name: string
  action: string
  status: string
  response_time_ms?: number
  http_status_code?: number
  error_message?: string
  timestamp: string
}

const IntegrationManagement: React.FC = () => {
  const [systems, setSystems] = useState<ExternalSystem[]>([])
  const [logs, setLogs] = useState<IntegrationLog[]>([])
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showLogs, setShowLogs] = useState(false)
  const [selectedSystem, setSelectedSystem] = useState<ExternalSystem | null>(null)
  const [formData, setFormData] = useState<Partial<ExternalSystem>>({
    name: '',
    system_type: 'HIS',
    vendor: '',
    version: '',
    endpoint_url: '',
    auth_type: 'API_KEY',
    timeout_seconds: 30,
    retry_attempts: 3,
    rate_limit_per_minute: null,
    status: 'INACTIVE',
    connection_config: {},
    auth_config: {}
  })

  useEffect(() => {
    fetchSystems()
    fetchOrganizations()
  }, [])

  const fetchSystems = async () => {
    try {
      const response = await fetch('/api/v1/integration/systems/', {
        cache: 'no-cache'
      })
      const data = await response.json()
      const systemList = Array.isArray(data) ? data : (data.results || [])
      setSystems(Array.isArray(systemList) ? systemList : [])
    } catch (error) {
      console.error('Failed to fetch systems:', error)
      setSystems([])
    } finally {
      setLoading(false)
    }
  }

  const fetchOrganizations = async () => {
    try {
      const response = await fetch('/api/v1/admin/organizations/')
      const data = await response.json()
      const orgList = Array.isArray(data) ? data : (data.results || [])
      setOrganizations(Array.isArray(orgList) ? orgList : [])
    } catch (error) {
      console.error('Failed to fetch organizations:', error)
      setOrganizations([])
    }
  }

  const fetchSystemLogs = async (systemId: number) => {
    try {
      const response = await fetch(`/api/v1/integration/systems/${systemId}/logs/`)
      const data = await response.json()
      const logList = Array.isArray(data) ? data : (data.results || [])
      setLogs(Array.isArray(logList) ? logList : [])
      setShowLogs(true)
    } catch (error) {
      console.error('Failed to fetch logs:', error)
      setLogs([])
    }
  }

  const handleCreateSystem = async () => {
    try {
      const response = await fetch('/api/v1/integration/systems/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        setShowModal(false)
        resetForm()
        fetchSystems()
      } else {
        const error = await response.json()
        alert('Failed to create system: ' + JSON.stringify(error))
      }
    } catch (error) {
      console.error('Error creating system:', error)
      alert('Failed to create system')
    }
  }

  const handleUpdateSystem = async () => {
    if (!selectedSystem?.id) return

    try {
      const response = await fetch(`/api/v1/integration/systems/${selectedSystem.id}/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        setShowModal(false)
        setSelectedSystem(null)
        resetForm()
        fetchSystems()
      } else {
        const error = await response.json()
        alert('Failed to update system: ' + JSON.stringify(error))
      }
    } catch (error) {
      console.error('Error updating system:', error)
      alert('Failed to update system')
    }
  }

  const handleTestConnection = async (systemId: number) => {
    try {
      const response = await fetch(`/api/v1/integration/systems/${systemId}/test_connection/`, {
        method: 'POST'
      })

      const result = await response.json()
      if (response.ok) {
        alert('✓ Connection test successful!')
        fetchSystems()
      } else {
        alert('✗ Connection test failed: ' + result.message)
      }
    } catch (error) {
      console.error('Error testing connection:', error)
      alert('Failed to test connection')
    }
  }

  const handleDeleteSystem = async (id: number) => {
    if (!confirm('Are you sure you want to delete this integration?')) return

    try {
      const response = await fetch(`/api/v1/integration/systems/${id}/`, {
        method: 'DELETE'
      })

      if (response.ok) {
        fetchSystems()
      } else {
        alert('Failed to delete system')
      }
    } catch (error) {
      console.error('Error deleting system:', error)
      alert('Failed to delete system')
    }
  }

  const openCreateModal = () => {
    resetForm()
    setSelectedSystem(null)
    setShowModal(true)
  }

  const openEditModal = (system: ExternalSystem) => {
    setSelectedSystem(system)
    setFormData({
      organization: system.organization,
      name: system.name,
      system_type: system.system_type,
      vendor: system.vendor,
      version: system.version,
      endpoint_url: system.endpoint_url,
      auth_type: system.auth_type,
      timeout_seconds: system.timeout_seconds,
      retry_attempts: system.retry_attempts,
      rate_limit_per_minute: system.rate_limit_per_minute,
      status: system.status,
      connection_config: system.connection_config,
      auth_config: system.auth_config
    })
    setShowModal(true)
  }

  const resetForm = () => {
    setFormData({
      name: '',
      system_type: 'HIS',
      vendor: '',
      version: '',
      endpoint_url: '',
      auth_type: 'API_KEY',
      timeout_seconds: 30,
      retry_attempts: 3,
      rate_limit_per_minute: null,
      status: 'INACTIVE',
      connection_config: {},
      auth_config: {}
    })
  }

  const getStatusBadge = (status: string) => {
    const colors = {
      ACTIVE: 'bg-green-100 text-green-800',
      INACTIVE: 'bg-gray-100 text-gray-800',
      TESTING: 'bg-yellow-100 text-yellow-800',
      ERROR: 'bg-red-100 text-red-800',
      MAINTENANCE: 'bg-blue-100 text-blue-800'
    }
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800'
  }

  const getHealthBadge = (health: string) => {
    const colors = {
      HEALTHY: 'bg-green-100 text-green-800',
      UNHEALTHY: 'bg-red-100 text-red-800',
      UNKNOWN: 'bg-gray-100 text-gray-800'
    }
    return colors[health as keyof typeof colors] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">HIS/LIS Integration</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage external system integrations (HIS, LIS, RIS, EMR). Each organization can configure their own connections.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={openCreateModal}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            ➕ Add Integration
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Name</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Organization</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Type</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Vendor</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Endpoint</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Health</th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {systems.map((system) => (
                    <tr key={system.id}>
                      <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                        {system.name}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {system.organization_name}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <span className="inline-flex rounded-full px-2 text-xs font-semibold leading-5 bg-blue-100 text-blue-800">
                          {system.system_type}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {system.vendor || '-'}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {system.endpoint_url || '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${getStatusBadge(system.status)}`}>
                          {system.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        {system.health_status && (
                          <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${getHealthBadge(system.health_status)}`}>
                            {system.health_status}
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleTestConnection(system.id!)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Test Connection"
                          >
                            🔌
                          </button>
                          <button
                            onClick={() => fetchSystemLogs(system.id!)}
                            className="text-green-600 hover:text-green-900"
                            title="View Logs"
                          >
                            📋
                          </button>
                          <button
                            onClick={() => openEditModal(system)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDeleteSystem(system.id!)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {systems.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-3 py-8 text-center text-sm text-gray-500">
                        No integrations configured. Click "Add Integration" to get started.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                {selectedSystem ? 'Edit Integration' : 'Add New Integration'}
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Organization</label>
                  <select
                    value={formData.organization}
                    onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="">Select Organization</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="e.g., HIS - Hospital A"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">System Type</label>
                  <select
                    value={formData.system_type}
                    onChange={(e) => setFormData({ ...formData, system_type: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="HIS">HIS - Hospital Information System</option>
                    <option value="LIS">LIS - Laboratory Information System</option>
                    <option value="RIS">RIS - Radiology Information System</option>
                    <option value="EMR">EMR - Electronic Medical Record</option>
                    <option value="HL7">HL7 System</option>
                    <option value="REST_API">REST API</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="TESTING">Testing</option>
                    <option value="MAINTENANCE">Maintenance</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Vendor</label>
                  <input
                    type="text"
                    value={formData.vendor}
                    onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Version</label>
                  <input
                    type="text"
                    value={formData.version}
                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Endpoint URL</label>
                  <input
                    type="text"
                    value={formData.endpoint_url}
                    onChange={(e) => setFormData({ ...formData, endpoint_url: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="https://his.hospital.com/api"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Auth Type</label>
                  <select
                    value={formData.auth_type}
                    onChange={(e) => setFormData({ ...formData, auth_type: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="API_KEY">API Key</option>
                    <option value="BASIC">Basic Auth</option>
                    <option value="OAUTH">OAuth</option>
                    <option value="JWT">JWT</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Timeout (seconds)</label>
                  <input
                    type="number"
                    value={formData.timeout_seconds}
                    onChange={(e) => setFormData({ ...formData, timeout_seconds: parseInt(e.target.value) })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Retry Attempts</label>
                  <input
                    type="number"
                    value={formData.retry_attempts}
                    onChange={(e) => setFormData({ ...formData, retry_attempts: parseInt(e.target.value) })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Rate Limit (per minute)</label>
                  <input
                    type="number"
                    value={formData.rate_limit_per_minute || ''}
                    onChange={(e) => setFormData({ ...formData, rate_limit_per_minute: e.target.value ? parseInt(e.target.value) : null })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3">
                <button
                  onClick={selectedSystem ? handleUpdateSystem : handleCreateSystem}
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                >
                  {selectedSystem ? 'Update' : 'Create'}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logs Modal */}
      {showLogs && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowLogs(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Integration Logs (Last 7 days)</h3>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Response Time</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">HTTP Code</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {logs.map((log) => (
                      <tr key={log.id}>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{log.action}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            log.status === 'SUCCESS' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {log.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          {log.response_time_ms ? `${log.response_time_ms}ms` : '-'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                          {log.http_status_code || '-'}
                        </td>
                        <td className="px-3 py-2 text-sm text-red-600 max-w-xs truncate">
                          {log.error_message || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-5">
                <button
                  onClick={() => setShowLogs(false)}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default IntegrationManagement
